/* Flash sale popup: see sections/flash-sale-popup.liquid */

if (!customElements.get('fs-popup')) {
  const STORAGE_KEY = 'fsPopupDismissedAt';

  const store = {
    get() {
      try { return parseInt(localStorage.getItem(STORAGE_KEY), 10) || 0; } catch (e) { return 0; }
    },
    set() {
      try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (e) { /* storage blocked */ }
    },
  };

  customElements.define(
    'fs-popup',
    class FsPopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('.fs-popup__dialog');
        this.onKeydown = this.onKeydown.bind(this);

        this.querySelectorAll('[data-fsp-close]').forEach((el) =>
          el.addEventListener('click', () => this.close(true))
        );
        this.querySelector('[data-fsp-cta]')?.addEventListener('click', () => {
          store.set(); // clicking "Order now" also counts as seen
          this.close(false, true);
        });

        // Theme editor: open while the section is selected, ignore the 24h rule
        if (this.dataset.designMode === 'true') {
          document.addEventListener('shopify:section:select', (e) => {
            if (e.detail.sectionId === this.dataset.sectionId) this.open();
          });
          document.addEventListener('shopify:section:deselect', (e) => {
            if (e.detail.sectionId === this.dataset.sectionId) this.close(false);
          });
          return;
        }

        if (!this.shouldShow()) return;
        const delay = (parseFloat(this.dataset.delay) || 0) * 1000;
        this.timer = setTimeout(() => this.open(), delay);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        document.removeEventListener('keydown', this.onKeydown);
        document.documentElement.classList.remove('fs-popup-open');
      }

      shouldShow() {
        const params = new URLSearchParams(window.location.search);
        if (params.get('fs_popup') === '1') return true; // manual test

        // Not on the page the popup links to
        try {
          const target = new URL(this.dataset.link, window.location.origin);
          const here = window.location.pathname.replace(/\/$/, '');
          if (target.origin === window.location.origin && target.pathname.replace(/\/$/, '') === here) return false;
        } catch (e) { /* bad link: still show */ }

        // Not after the sale has ended
        const end = new Date(this.dataset.end).getTime();
        if (this.dataset.end && !isNaN(end) && Date.now() >= end) return false;

        // Not again within N hours of being closed
        const hours = parseFloat(this.dataset.hours) || 24;
        const last = store.get();
        if (last && Date.now() - last < hours * 3600 * 1000) return false;

        return true;
      }

      open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.lastFocus = document.activeElement;
        // load the (lazy) image now that the popup is about to show
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
        this.hidden = false;
        document.documentElement.classList.add('fs-popup-open');
        // force a reflow so the entrance transition runs (rAF pauses in background tabs)
        void this.offsetWidth;
        this.classList.add('is-open');
        document.addEventListener('keydown', this.onKeydown);
        setTimeout(() => this.querySelector('.fs-popup__close')?.focus({ preventScroll: true }), 350);
      }

      close(remember, navigating) {
        if (!this.isOpen) return;
        this.isOpen = false;
        if (remember && this.dataset.designMode !== 'true') store.set();
        document.removeEventListener('keydown', this.onKeydown);
        if (navigating) return; // the page is about to change; leave it as is
        this.classList.remove('is-open');
        document.documentElement.classList.remove('fs-popup-open');
        setTimeout(() => {
          if (!this.isOpen) this.hidden = true;
        }, 400);
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      }

      onKeydown(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close(true);
          return;
        }
        if (e.key !== 'Tab') return;
        // keep keyboard focus inside the popup
        const focusables = Array.from(
          this.dialog.querySelectorAll('a[href], button:not([disabled])')
        ).filter((el) => el.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  );
}
